import { FileText } from 'lucide-react';
import { fmtDate } from '@/lib/dates';
import type { DoaVersionRow, DoaVersionStatus } from '@/api/client';

/**
 * Sprint-10 — Left-rail version list for the DoA page.
 *
 * Pure presentation component: receives the full version array and renders one
 * card per version with status badge + signed/total counts. Click hands the
 * version_id up to the parent.
 *
 * The signed_count / total_count are NOT on DoaVersionRow itself — they live
 * on the per-version detail payload (rows[]). The list shows just the version
 * label + status until the parent passes counts; counts are intentionally
 * optional so the rail renders even before the detail query resolves.
 */

const STATUS_BADGE_CLASS: Record<DoaVersionStatus, string> = {
  draft:      'badge badge-neutral',
  in_review:  'badge badge-warning',
  active:     'badge badge-success',
  superseded: 'badge badge-info',
  archived:   'badge badge-neutral',
};

function formatStatus(s: DoaVersionStatus): string {
  return s.replace(/_/g, ' ');
}

function formatVersionDate(v: DoaVersionRow): string {
  const start = v.effective_at ?? v.created_at;
  if (!start) return '—';
  const startStr = fmtDate(start);
  return v.effective_to ? `${startStr} – ${fmtDate(v.effective_to)}` : startStr;
}

export interface DoaVersionListProps {
  binderId: string;
  versions: DoaVersionRow[];
  selectedVersionId: string | null;
  onSelect: (versionId: string) => void;
  /**
   * Optional per-version signed/total tuples — only the currently-selected
   * version is hydrated by the parent (it has the detail query open). Falls
   * back to a "—/—" placeholder for un-hydrated rows.
   */
  signedCountsByVersionId?: Record<string, { signed: number; total: number }>;
}

export function DoaVersionList({
  versions,
  selectedVersionId,
  onSelect,
  signedCountsByVersionId,
}: DoaVersionListProps) {
  if (versions.length === 0) {
    return (
      <div className="card flex flex-col items-center justify-center gap-2 p-6 text-center text-xs text-ink-muted">
        <FileText className="h-5 w-5 opacity-50" />
        <span>No DoA versions yet.</span>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {versions.map((v) => {
        const isSelected = v.doa_version_id === selectedVersionId;
        const counts = signedCountsByVersionId?.[v.doa_version_id];
        return (
          <li key={v.doa_version_id}>
            <button
              type="button"
              onClick={() => onSelect(v.doa_version_id)}
              className={[
                'w-full rounded border px-3 py-2 text-left transition',
                isSelected
                  ? 'border-primary-300 bg-primary-50 ring-1 ring-primary-200'
                  : 'border-surface-border bg-surface-base hover:bg-surface-muted/60',
              ].join(' ')}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium text-ink-primary">
                  {v.reference_number ?? `v${v.version_number}`}
                </span>
                <span className={`${STATUS_BADGE_CLASS[v.status]} capitalize text-[10px]`}>
                  {formatStatus(v.status)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-ink-muted">
                <span className="truncate">{formatVersionDate(v)}</span>
                <span className="shrink-0 tabular-nums">
                  {counts ? `${counts.signed}/${counts.total} signed` : '—/—'}
                </span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
